import { CATEGORY_LABEL, DATA_TYPE_LABEL, HANDLE_LABEL, NODE_TEXT, PARAM_TEXT } from "@globals/automations";
import { formatDecimal } from "@globals/format";

/**
 * Centrální slovník user-facing stringů (pouze čeština).
 */
export const t = {
  app: {
    name: "Demirel",
    loading: "Načítání…",
    error: "Něco se pokazilo",
    retry: "Zkusit znovu",
    close: "Zavřít",
    cancel: "Zrušit",
    save: "Uložit",
    delete: "Smazat",
    add: "Přidat",
    edit: "Upravit",
    confirm: "Potvrdit",
    yes: "Ano",
    no: "Ne",
  },
  auth: {
    login: "Přihlášení",
    logout: "Odhlásit",
    register: "Registrace",
    username: "Přihlašovací jméno",
    password: "Heslo",
    passwordConfirm: "Heslo znovu",
    loginRequired: "Pro přístup je nutné se přihlásit.",
    invalidCredentials: "Nesprávné jméno nebo heslo.",
    accountLocked: "Účet je dočasně uzamčen, zkus to později.",
    loginSuccess: "Přihlášení proběhlo úspěšně.",
    loginFailed: "Přihlášení selhalo.",
    fillAllFields: "Vyplň všechna pole.",
    passwordMismatch: "Hesla se neshodují.",
  },
  dashboard: {
    title: "Dashboard",
    chartLock: "Zamknout grafy",
    settings: "Nastavení",
    editMode: "Upravit rozložení",
    saveLayout: "Uložit rozložení",
    cancelEdit: "Zrušit úpravy",
    resetLayout: "Obnovit výchozí",
    addWidget: "Přidat widget",
    noData: "Žádná data k zobrazení",
    sensorOffline: "Senzor je offline (poslední data před chvílí)",
  },
  settings: {
    title: "Nastavení",
    tabs: {
      preferences: "Preference",
      rules: "Pravidla notifikací",
      picow: "Konfigurace PicoW",
      routeros: "AP (MikroTik)",
      foscam: "Kamery",
      diagnostics: "Diagnostika",
      push: "Push notifikace",
    },
    /** Karta Nastavení → AP (MikroTik): SSH konzole a soubory na AP. */
    routerosPanel: {
      onlyAdmin: "Konzole k AP je dostupná jen administrátorům — dá se přes ni přenastavit celá síť.",
      consoleTitle: "Konzole",
      consoleHint: "Plnohodnotný terminál RouterOS přes SSH. Ctrl+C i historie šipkami fungují.",
      connect: "Připojit",
      disconnect: "Odpojit",
      reconnect: "Připojit znovu",
      stateIdle: "Nepřipojeno",
      stateConnecting: "Připojuji…",
      stateReady: "Připojeno",
      stateClosed: "Spojení ukončeno",
      /** Klíčové: každý řádek jde do audit logu, ať to nikoho nepřekvapí. */
      auditNotice: "Zadané příkazy se zaznamenávají do audit logu (hesla se maskují).",
      notConfigured: "AP není nakonfigurované — chybí ROUTEROS_SSH_USER.",

      filesTitle: "Soubory na AP",
      filesHint: "Konfigurace vyexportovaná na AP (`/export file=jmeno`) a zálohy.",
      filesEmpty: "Na AP nejsou žádné soubory ke stažení.",
      filesLoadFailed: "Seznam souborů se nepodařilo načíst.",
      colName: "Soubor",
      colSize: "Velikost",
      colMtime: "Změněno",
      mtimeUnknown: "neznámo",
      download: "Stáhnout",
      downloadFailed: "Stažení selhalo.",
      upload: "Nahrát soubor",
      uploading: "Nahrávám…",
      uploaded: "Soubor nahraný na AP.",
      uploadFailed: "Nahrání selhalo.",
      /** Nejdůležitější věta panelu — bez ní by lidi čekali, že se konfigurace použije sama. */
      uploadNotice: "Nahraný soubor se sám NEPOUŽIJE. Aplikuje se až příkazem /import v konzoli.",
      uploadTooBig: (max: string) => `Soubor je větší než ${max}.`,
      uploadBadExt: "Nahrát jde jen .rsc, .backup nebo .txt.",
      uploadConflict: "Soubor s tímhle jménem na AP už je — přejmenuj ho.",
    },

    picowPanel: {
      onlyAdmin: "Konfigurace zařízení je dostupná jen administrátorům — jsou v ní hesla k WiFi a MQTT.",
      empty: "Nenašlo se žádné zařízení.",
      loadFailed: "Konfigurace se nepodařilo načíst.",
      unreadable: "Konfiguraci tohoto zařízení nelze přečíst",
      save: "Uložit",
      saving: "Ukládám…",
      saved: "Konfigurace uložena.",
      saveFailed: "Uložení selhalo.",
      push: "Odeslat do zařízení",
      pushing: "Odesílám…",
      pushed: "Odesláno, zařízení se restartuje.",
      pushFailed: "Odeslání selhalo.",
      pushTitle: "Odeslat konfiguraci do zařízení?",
      pushBody:
        "Zařízení si stáhne uloženou konfiguraci a restartuje se. Odesílá se to, co je uložené na serveru — ne rozepsané změny v editoru.",
      unsavedWarning: "Máš neuložené změny. Odešle se poslední uložená verze.",
      ioOrderTitle: "Změnilo se pořadí pinů",
      ioOrderBody:
        "Hodnoty se párují s názvy podle pořadí, takže než se zařízení restartuje, budou chvíli u nesprávných veličin. Uložit i tak?",
      conflict: "Konfigurace se mezitím změnila jinde. Načti panel znovu.",
      validating: "Kontroluji…",
      valid: "Konfigurace je v pořádku",
      issues: "Nalezené problémy",
      errors: "Chyby",
      warnings: "Varování",
      blocked: "Dokud jsou v konfiguraci chyby, nejde uložit ani odeslat.",
      history: "Historie verzí",
      historyEmpty: "Zatím žádné starší verze.",
      historyLoad: "Načíst do editoru",
      historyLoaded: "Záloha načtená do editoru. Zkontroluj ji a ulož.",
      historyBanner: "Prohlížíš zálohu z",
    },
    darkMode: "Tmavý režim",
    notifications: "Notifikace",
    enableNotifications: "Povolit notifikace",
    notificationsAllowed: "Notifikace povoleny",
    notificationsBlocked: "Notifikace zablokovány v prohlížeči",
    restartServer: "Restartovat server",
    restartConfirm: "Opravdu chceš restartovat server?",
    push: {
      title: "Push notifikace",
      description: "Spravuj push notifikace na svých zařízeních.",
      permissionGranted: "Povoleno v prohlížeči",
      permissionDenied: "Zablokováno v prohlížeči — povol je v nastavení prohlížeče",
      permissionDefault: "Zatím nepovoleno",
      enable: "Povolit na tomto zařízení",
      disable: "Vypnout na tomto zařízení",
      test: "Poslat testovací notifikaci",
      devices: "Přihlášená zařízení",
      devicesHint: "Prohlížeče a PWA přihlášené pod tvým účtem — sem chodí jen tvoje notifikace.",
      noDevices: "Žádná přihlášená zařízení.",
      removeDevice: "Odebrat",
      thisDevice: "Toto zařízení",
      lastSuccess: "Naposledy doručeno",
      neverDelivered: "Zatím nedoručeno",
      failures: "Selhání",
      iosInstallHint:
        "Na iPhonu/iPadu nejdřív přidej appku na plochu (Sdílet → Přidat na plochu), pak povol notifikace.",
      unsupported: "Tento prohlížeč push notifikace nepodporuje.",
      channels: {
        title: "Notifikace na mobil (i iPhone / Brave)",
        description:
          "Web push na iOS funguje jen v appce přidané na plochu přes Safari. Pro spolehlivé notifikace na iPhonu (i v Brave) použij ntfy nebo Telegram — push pošle jejich appka.",
        groupTitle: "Kanály na mobil",
        groupHint: "Všechny se vážou na tvůj účet — chodí do nich jen tvoje notifikace.",
        ntfyTitle: "ntfy",
        ntfyOwn: "Tvůj topic",
        ntfyServer: "Server",
        ntfyTopic: "Topic",
        ntfyOwnHint: "Topic je odvozený z tvého přihlašovacího jména — nemění se a nemusíš ho nikde zapínat.",
        ntfyHelp:
          "Nainstaluj appku ntfy (App Store / Google Play) → Settings → Default server nastav na server výše → přihlas se svým účtem (anonymně to neprojde) → Subscribe to topic = topic výše.",
        ntfyAccessHint:
          "Když se topic nedá odebírat, chybí ti na ntfy serveru přístup — o ten si řekni správci.",
        telegramTitle: "Telegram",
        telegramConnect: "Připojit Telegram",
        telegramConnected: "Telegram připojen",
        telegramDisable: "Odpojit Telegram",
        telegramHelp: "Otevře se bot v Telegramu — klikni Start. Pak ti alerty budou chodit do Telegramu.",
        barkTitle: "Bark (iOS)",
        barkServer: "Server",
        barkSave: "Uložit klíč",
        barkConnected: "Bark připojen",
        barkDisable: "Odpojit Bark",
        barkPlaceholder: "Device Key z Bark appky",
        barkHelp:
          "Nainstaluj appku Bark (App Store) → v appce nastav Server na URL výše → zkopíruj svůj Device Key a vlož sem.",
        copy: "Kopírovat",
        copied: "Zkopírováno",
        test: "Poslat test",
        notConfigured: "Tento kanál není na serveru nakonfigurovaný.",
      },
    },
    /** Karta Nastavení → Kamery. Ovládání, které nemá co dělat nad živým obrazem. */
    foscam: {
      title: "Kamery",
      description: "Kodek, rychlost otáčení a správa pozic. Ovládání kamery je v jejím fullscreen zobrazení.",
      none: "Žádné kamery nejsou nakonfigurované.",
      onlyRpi: "Ovládání kamer je dostupné jen na RPi — tady se zobrazuje jen video.",
      offline: "Kamera neodpovídá na ovládání (CGI). Video může jet dál.",
      capsPtz: "PTZ",
      capsZoom: "ZOOM",
      capsPresets: "POZICE",
      codecCurrent: "Aktuální kodek",
      codecConfirmTitle: "Přepnout kodek?",
      codecConfirmBody: "Video stream se restartuje, obraz bude pár sekund černý.",
      codecUnavailable: "Účet na kameře nemá práva měnit formát streamu.",
      speedLabel: "Rychlost otáčení",
      speedHint: "0 = nejrychlejší, 4 = nejpomalejší.",
      speedSaved: "Rychlost nastavena.",
      presetsNone: "Kamera nemá uložené žádné pozice.",
      presetsHint: "Novou pozici uložíš u kamery — nejdřív ji tam namíříš.",
      stream: "Stream",
      streamUptime: "Běží",
      streamFailures: "Selhání",
      streamState: "Stav",
      streamBitrate: "Tok",
      streamIp: "IP",
      streamLastData: "Poslední data",
      // ── Restart kamery ──
      reboot: "Restartovat kameru",
      rebootConfirmTitle: "Restartovat kameru?",
      rebootConfirmBody:
        "Kamera bude přibližně minutu nedostupná a znovu se připojí k wifi. Obraz se sám vrátí, až naběhne.",
      rebootStarted: "Kamera se restartuje.",
      // ── Kvalita hlavního streamu ──
      streamQuality: "Kvalita hlavního streamu",
      streamQualityHint: "Vyšší rozlišení = ostřejší obraz, ale větší datový tok.",
      resolutionCurrent: "Aktuální rozlišení",
      resolutionConfirmTitle: "Změnit rozlišení?",
      resolutionConfirmBody: "Video stream se přenastaví, obraz bude pár sekund černý.",
      // ── Obraz ──
      image: "Obraz",
      brightness: "Jas",
      contrast: "Kontrast",
      hue: "Odstín",
      saturation: "Sytost",
      sharpness: "Ostrost",
      denoise: "Odšumění",
      denoiseReadonly: "Kamera neumožňuje nastavit přes CGI — jen zobrazení.",
      resetImage: "Reset",
      saved: "Uloženo.",
      // ── OSD (popisky v obraze) ──
      osd: "Popisky v obraze (OSD)",
      osdHint: "Co se vypaluje do obrazu. Klikni pro zapnutí/vypnutí.",
      timestamp: "Časové razítko",
      devName: "Název kamery",
      tempHumid: "Teplota a vlhkost",
      // ── Zrcadlení / překlopení ──
      mirror: "Zrcadlení / překlopení",
      mirrorHint: "Pozor — otočí obraz. Klikni pro zapnutí/vypnutí.",
      mirrorH: "Zrcadlit vodorovně",
      flipV: "Překlopit svisle",
      // ── Noční přisvícení (IR) ──
      ir: "Noční přisvícení (IR)",
      irHint: "Auto zapne IR podle světla; ručně můžeš vynutit zapnuto/vypnuto.",
      irAuto: "Auto",
      irOn: "Zapnuto",
      irOff: "Vypnuto",
      // ── HDR ──
      hdr: "HDR",
      hdrHint: "Vyrovnává přepaly a tmavá místa v protisvětle.",
      hdrOn: "Zapnuto",
      hdrOff: "Vypnuto",
    },
  },
  rules: {
    title: "Pravidla notifikací",
    addNew: "Přidat pravidlo",
    name: "Název pravidla",
    notificationTitle: "Nadpis v oznámení",
    notificationBody: "Popisek v oznámení",
    severity: "Závažnost",
    severityInfo: "INFO",
    severityWarning: "WARNING",
    severitySerious: "SERIOUS",
    conditions: "Podmínky",
    addCondition: "Přidat podmínku",
    syntaxError: "Chyba syntaxe v podmínkách.",
    saved: "Pravidla uložena.",
    saveError: "Nepodařilo se uložit pravidla.",
    statusNew: "nové",
    statusEdited: "upraveno",
    statusDeleted: "smazáno",
  },
  status: {
    connected: "Připojeno",
    connecting: "Připojuji se…",
    disconnected: "Odpojeno",
    online: "Online",
    offline: "Offline",
    error: "Chyba",
  },
  errors: {
    generic: "Neočekávaná chyba",
    network: "Server není dostupný. Zkontroluj připojení.",
    csrf: "Bezpečnostní token vypršel. Zkus to znovu.",
    unauthorized: "Nemáš oprávnění k této akci.",
    sessionExpired: "Tvoje přihlášení vypršelo, přihlas se znovu.",
  },
  devices: {
    title: "Zařízení",
    addDevice: "Přidat zařízení",
    noDevices: "Žádná zařízení",
    noDevicesHint: "Přidej první kliknutím na + Přidat zařízení",
    tabs: {
      bms: "BMS",
      pip: "Měnič",
      tracer: "Solární regulátor",
    },
    badge: {
      bms: "BMS",
      pip: "PIP",
      tracer: "TRACER",
    },
  },
  foscam: {
    controls: "Ovládání kamery",
    move: "Pohyb",
    stop: "Stop",
    zoomIn: "Přiblížit",
    zoomOut: "Oddálit",
    presets: "Presety",
    presetGoto: "Přejet na pozici",
    presetSave: "Uložit aktuální pozici",
    presetSaveTitle: "Uložit preset",
    presetName: "Název pozice",
    presetDelete: "Smazat preset",
    presetDeleteConfirm: "Opravdu smazat preset",
    codec: "Kodek",
    codecSwitch: "Přepnout kodek",
    codecNote: "Přepnutí restartuje video stream.",
    speed: "Rychlost",
    unavailable: "Kamera není dostupná",
    accessDenied: "Účet nemá na kameře dostatečná práva",
    saved: "Uloženo",
    deleted: "Smazáno",
    // ── spotlight / fullscreen ──
    spotlight: "Zobrazit na celou obrazovku",
    spotlightClose: "Zavřít celou obrazovku",
    // ── digitální zoom (výřez v obraze, nehýbe kamerou) ──
    digitalZoomIn: "Přiblížit výřez",
    digitalZoomOut: "Oddálit výřez",
    zoomReset: "Zrušit přiblížení",
    zoomHint: "Digitální přiblížení — kolečkem nebo dvěma prsty, tažením se posouvá výřez.",
    // ── míření (dvojklik otočí kameru tak, aby bylo kliknuté místo uprostřed) ──
    aimHint: "Dvojklik do obrazu otočí kameru na to místo.",
    aimBusy: "Otáčím kamerou…",
    aimLimit: (dir: string) => {
      const kam =
        dir === "Left" ? "doleva" : dir === "Right" ? "doprava" : dir === "Up" ? "nahoru" : "dolů";
      return `Kamera je na dorazu — dál ${kam} už se neotočí.`;
    },
    aimUnverified: "Kamera se otočila, ale výsledek nešlo ověřit z obrazu (tma nebo nedostupný stream).",
    aimPartial: "Kamera se otočila jen částečně — zkus dvojklik znovu.",
    zoomLevel: (scale: number) => `${formatDecimal(scale, 1)}×`,
    // ── rozložení mřížky ──
    layoutToQuad: "Přepnout na mřížku 2×2",
    layoutToAuto: "Přepnout na vedle sebe",
    // ── zpětná vazba při ovládání ──
    latency: "Zpoždění obrazu",
    transportHint: "Jak se obraz přenáší a o kolik je pozadu za skutečností.",
    streamError: "Stream není dostupný.",
    streamRetry: "Zkusit znovu",
    holdHint: "Drž pro pohyb",
    stopFailed: "Zastavení kamery se nepodařilo — zkus tlačítko Stop. Kamera se možná pořád otáčí.",
    codecUnsupported: "Tenhle prohlížeč neumí dekódovat kodek této kamery.",
    codecUnsupportedHint: "Přepni ho v Nastavení → Kamery.",
    controlUnavailable: "Kamera nereaguje na ovládání.",
    controlOnlyRpi: "Ovládání kamer je dostupné jen na RPi.",
  },
  bms: {
    title: "Bateriový management",
    overview: "Přehled",
    addDevice: "Přidat BMS",
    scan: "Vyhledat BLE",
    scanning: "Vyhledávám…",
    noDevices: "Žádný BMS není nakonfigurován",
    soc: "Stav nabití",
    voltage: "Napětí packu",
    current: "Proud",
    currentState: {
      charging: "Nabíjení",
      discharging: "Vybíjení",
      idle: "V klidu",
    },
    power: "Výkon",
    cellCount: "Počet článků",
    cellVoltages: "Napětí článků",
    cellMin: "Min",
    cellMax: "Max",
    cellAvg: "Průměr",
    cellDelta: "Rozdíl",
    temperature: "Teplota",
    tempT1: "Teplota T1",
    tempT2: "Teplota T2",
    tempMos: "Teplota MOS",
    cycles: "Cykly",
    remaining: "Zbývající kapacita",
    fullCapacity: "Plná kapacita",
    chargeMosfet: "Nabíjecí MOSFET",
    dischargeMosfet: "Vybíjecí MOSFET",
    balancer: "Balancer",
    on: "Zapnuto",
    off: "Vypnuto",
    alarms: "Alarmy",
    noAlarms: "Bez alarmů",
    status: {
      idle: "Nečinný",
      connecting: "Připojuji se…",
      connected: "Připojeno",
      reconnecting: "Obnovuji spojení…",
      stopped: "Zastaveno",
      error: "Chyba",
    },
    deviceInfo: "Informace o zařízení",
    settings: "Nastavení BMS",
    settingsWarning: "Změna nastavení může poškodit baterii nebo způsobit požár. Postupuj OPATRNĚ.",
    controls: "Ovládání",
    controlsWarning: "Ovládání MOSFETů odpojí nabíjení nebo vybíjení. Připojené zařízení může náhle ztratit napájení.",
    confirmTitle: "Opravdu provést tuto akci?",
    confirmDescription: "Akce se okamžitě projeví na fyzickém BMS. Po potvrzení už nelze vrátit zpět bez další akce.",
    confirmYes: "Ano, provést",
    readOnlyWarning: "BMS je v READ-ONLY módu (BMS_READ_ONLY=true). Ovládání není k dispozici.",
    adminRequired: "Pro ovládání BMS je nutný účet s admin oprávněním.",
    rpiRequired: "BMS dostupný pouze v režimu RUNNER=rpi.",
    fields: {
      slug: "Identifikátor (slug)",
      name: "Název",
      transport: "Připojení",
      mac: "MAC adresa",
      host: "Host (IP)",
      port: "Port",
      serialPath: "Cesta k sériovému portu",
      capacity: "Kapacita (mAh)",
      chemistry: "Typ chemie",
      cellOvp: "Cell OVP (mV)",
      cellOvr: "Cell OVR (mV)",
      cellUvp: "Cell UVP (mV)",
      cellUvr: "Cell UVR (mV)",
      balanceDelta: "Balance delta (mV)",
      balanceStart: "Balance start (mV)",
      chargeOcp: "Charge OCP (A)",
      dischargeOcp: "Discharge OCP (A)",
    },
    actions: {
      reconnect: "Obnovit spojení",
      delete: "Smazat BMS",
      saveSettings: "Uložit nastavení",
    },
  },
  pip: {
    title: "Měnič",
    addDevice: "Přidat měnič",
    noDevices: "Žádný měnič není nakonfigurován",
    detail: {
      tabs: {
        flow: "Energy Flow",
        detail: "Detail",
      },
      overview: "Přehled",
      alarms: "Alarmy a varování",
      noAlarms: "Žádná aktivní varování",
      controls: "Ovládání",
      settings: "Nastavení",
      deviceInfo: "Informace o zařízení",
    },
    status: {
      idle: "Nečinný",
      connecting: "Připojuji se…",
      connected: "Připojeno",
      reconnecting: "Obnovuji spojení…",
      stopped: "Zastaveno",
      error: "Chyba",
    },
    mode: {
      P: "Zapnuto",
      S: "Standby",
      L: "Síť (passthrough)",
      B: "Baterie (off-grid)",
      F: "Porucha",
      H: "Eco / úspora",
      UNKNOWN: "Neznámý mód",
    },
    metrics: {
      gridV: "Síť (V)",
      gridHz: "Frekvence sítě (Hz)",
      acOutV: "Výstup (V)",
      acOutHz: "Frekvence výstupu (Hz)",
      acOutVa: "Zdánlivý výkon (VA)",
      acOutW: "Činný výkon (W)",
      load: "Zatížení (%)",
      busV: "BUS DC (V)",
      heatsink: "Teplota chladiče (°C)",
      batteryV: "Baterie (V)",
      batterySoc: "Stav baterie (%)",
      batteryChargeA: "Nabíjecí proud (A)",
      batteryDischargeA: "Vybíjecí proud (A)",
      pvV: "PV (V)",
      pvA: "PV (A)",
      pvW: "PV (W)",
      pv2V: "PV2 (V)",
      pv2A: "PV2 (A)",
      pv2W: "PV2 (W)",
    },
    controls: {
      outputPriority: "Priorita výstupu",
      outputPriorityOptions: {
        "00": "Síť → Solar → Baterie",
        "01": "Solar → Síť → Baterie",
        "02": "Solar → Baterie → Síť (SBU)",
      },
      chargerPriority: "Priorita nabíjení",
      chargerPriorityOptions: {
        "00": "Síť",
        "01": "Solar",
        "02": "Solar + Síť",
        "03": "Jen solar",
      },
      maxChargingCurrent: "Max. nabíjecí proud (A)",
      maxUtilityCurrent: "Max. proud ze sítě (A)",
      readOnlyWarning: "Měnič je v READ-ONLY módu (PIP_READ_ONLY=true). Ovládání není k dispozici.",
      adminRequired: "Pro ovládání měniče je nutný účet s admin oprávněním.",
    },
    settings: {
      batteryType: "Typ baterie",
      shutdownV: "Cut-off napětí (V)",
      bulkV: "Bulk napětí (V)",
      floatV: "Float napětí (V)",
      rechargeV: "Re-charge napětí (V)",
      redischargeV: "Re-discharge napětí (V)",
      gridRange: "AC vstup rozsah",
      gridRangeOptions: {
        APPLIANCE: "Appliance (90–280 V)",
        UPS: "UPS (170–280 V)",
      },
      outputMode: "Výstupní režim",
      outputModeOptions: {
        SINGLE: "Jediná jednotka",
        PARALLEL: "Paralelně",
        PHASE_1_OF_3: "L1 of 3",
        PHASE_2_OF_3: "L2 of 3",
        PHASE_3_OF_3: "L3 of 3",
      },
      saveButton: "Uložit nastavení",
      warning: "Špatně nastavené napětí může poškodit baterii. Postupuj OPATRNĚ.",
    },
    add: {
      tabs: {
        serial: "Sériová linka",
        tcp: "TCP (vestavěné WiFi)",
      },
      probe: "Detekovat",
      probing: "Detekuji…",
      probeSuccess: "Měnič detekován",
      probeFailed: "Detekce selhala",
      fields: {
        slug: "Identifikátor (slug)",
        name: "Název",
        serialPath: "Cesta k sériovému portu",
        baudRate: "Baud rate",
        host: "Host (IP)",
        port: "Port",
        protocolVariant: "Protokol",
        batteryType: "Typ baterie",
        batteryNominalV: "Nominální napětí baterie (V)",
      },
    },
    actions: {
      reconnect: "Obnovit spojení",
      delete: "Smazat měnič",
      saveSettings: "Uložit nastavení",
    },
  },
  tracer: {
    title: "Nabíječ",
    addDevice: "Přidat nabíječ",
    noDevices: "Žádný nabíječ není nakonfigurován",
    detail: {
      tabs: {
        flow: "Energy Flow",
        detail: "Detail",
      },
      overview: "Přehled",
      stats: "Statistiky",
      alarms: "Alarmy a varování",
      noAlarms: "Žádná aktivní varování",
      controls: "Ovládání",
      settings: "Nastavení",
      deviceInfo: "Informace o zařízení",
    },
    status: {
      idle: "Nečinný",
      connecting: "Připojuji se…",
      connected: "Připojeno",
      reconnecting: "Obnovuji spojení…",
      stopped: "Zastaveno",
      error: "Chyba",
    },
    stage: {
      NO_CHARGE: "Nenabíjí",
      FLOAT: "Float",
      BOOST: "Boost",
      EQUALIZE: "Vyrovnávání",
      UNKNOWN: "Neznámý",
    },
    metrics: {
      pvV: "PV (V)",
      pvA: "PV (A)",
      pvW: "PV výkon (W)",
      batteryV: "Baterie (V)",
      batterySoc: "SOC (%)",
      batteryChargeA: "Nabíjecí proud (A)",
      batteryChargeW: "Nabíjecí výkon (W)",
      batteryNetA: "Net proud (A)",
      batteryTemp: "Teplota baterie (°C)",
      remoteBatteryTemp: "Teplota baterie (remote, °C)",
      internalTemp: "Vnitřní teplota (°C)",
      heatsinkTemp: "Teplota chladiče (°C)",
      ambientTemp: "Okolní teplota (°C)",
      loadV: "Load (V)",
      loadA: "Load (A)",
      loadW: "Load výkon (W)",
    },
    stats: {
      pvVoltageMaxToday: "Max PV dnes",
      pvVoltageMinToday: "Min PV dnes",
      batteryVoltageMaxToday: "Max baterie dnes",
      batteryVoltageMinToday: "Min baterie dnes",
      energyGenToday: "Vyrobeno dnes",
      energyGenMonth: "Vyrobeno měsíc",
      energyGenYear: "Vyrobeno rok",
      energyGenTotal: "Vyrobeno celkem",
      energyConsToday: "Spotřebováno dnes",
      energyConsMonth: "Spotřebováno měsíc",
      energyConsYear: "Spotřebováno rok",
      energyConsTotal: "Spotřebováno celkem",
      co2Reduction: "Úspora CO₂",
    },
    batteryType: {
      USER: "User-defined",
      SEALED: "Sealed",
      GEL: "GEL",
      FLOODED: "Flooded",
      LITHIUM: "Lithium",
    },
    controls: {
      manualLoad: "Manuální load",
      forceLoad: "Force load",
      testMode: "Test mode",
      readOnlyWarning: "Nabíječ je v READ-ONLY módu (TRACER_READ_ONLY=true). Ovládání není k dispozici.",
      adminRequired: "Pro ovládání nabíječe je nutný účet s admin oprávněním.",
    },
    settings: {
      batteryType: "Typ baterie",
      batteryCapacity: "Kapacita baterie (Ah)",
      tempCompCoef: "Teplotní kompenzace (mV/°C/2V)",
      highVoltageDisconnect: "High voltage disconnect (V)",
      chargingLimitVoltage: "Charging limit (V)",
      overVoltageReconnect: "Over voltage reconnect (V)",
      equalizationVoltage: "Equalization (V)",
      boostVoltage: "Boost (V)",
      floatVoltage: "Float (V)",
      boostReconnectVoltage: "Boost reconnect (V)",
      lowVoltageReconnect: "Low voltage reconnect (V)",
      underVoltageRecover: "Under voltage recover (V)",
      underVoltageWarning: "Under voltage warning (V)",
      lowVoltageDisconnect: "Low voltage disconnect (V)",
      dischargingLimitVoltage: "Discharging limit (V)",
      eqChargingCycleDays: "Equalization cycle (dny)",
      batTempWarnHigh: "Battery temp warn high (°C)",
      batTempWarnLow: "Battery temp warn low (°C)",
      eqDurationMin: "Equalization duration (min)",
      boostDurationMin: "Boost duration (min)",
      saveButton: "Uložit nastavení",
      warning: "Špatně nastavené napětí může poškodit baterii. Postupuj OPATRNĚ.",
    },
    add: {
      probe: "Detekovat",
      probing: "Detekuji…",
      probeSuccess: "Nabíječ detekován",
      probeFailed: "Detekce selhala",
      fields: {
        slug: "Identifikátor (slug)",
        name: "Název",
        serialPath: "Cesta k sériovému portu",
        slaveId: "Modbus slave ID",
        baudRate: "Baud rate",
        batteryNominalV: "Nominální napětí baterie (V)",
        batteryType: "Typ baterie",
        batteryCapacity: "Kapacita baterie (Ah, volitelné)",
      },
    },
    alarms: {
      BAT_OVERVOLT: "Baterie přepětí",
      BAT_UNDERVOLT: "Baterie podpětí",
      BAT_LOW_DISCONNECT: "Baterie odpojena (LVD)",
      BAT_FAULT: "Baterie porucha",
      BAT_HIGH_TEMP: "Baterie vysoká teplota",
      BAT_LOW_TEMP: "Baterie nízká teplota",
      BAT_INNER_RESISTANCE: "Vnitřní odpor baterie abnormální",
      WRONG_RATED_VOLTAGE_ID: "Špatně rozpoznané napětí",
      PV_NO_POWER: "PV bez napájení",
      PV_HIGHER_VOLT: "PV vyšší napětí",
      PV_INPUT_ERROR: "PV vstup chyba",
      CHARGING_MOSFET_SHORT: "Charging MOSFET zkrat",
      CHARGING_OR_ANTI_REVERSE_MOSFET_SHORT: "Charging / Anti-reverse MOSFET zkrat",
      ANTI_REVERSE_MOSFET_SHORT: "Anti-reverse MOSFET zkrat",
      INPUT_OVER_CURRENT: "Vstupní nadproud",
      LOAD_OVER_CURRENT: "Load nadproud",
      LOAD_SHORT: "Load zkrat",
      LOAD_MOSFET_SHORT: "Load MOSFET zkrat",
      PV_INPUT_SHORT: "PV vstup zkrat",
      FAULT: "Porucha",
    },
    actions: {
      reconnect: "Obnovit spojení",
      delete: "Smazat nabíječ",
      saveSettings: "Uložit nastavení",
    },
  },
  automations: {
    title: "Automatizace",
    tabAutomations: "Automatizace",
    tabComputed: "Vlastní veličiny",
    addNew: "Nová automatizace",
    empty: "Zatím žádné automatizace.",
    enabled: "Zapnuto",
    disabled: "Vypnuto",
    lastRun: "Poslední běh",
    neverRan: "Zatím neběželo",
    migrated: "převedeno automaticky",
    runsCount: (n: number) => `${n}× vyhodnoceno`,
    delete: "Smazat",
    deleteConfirm: "Smazat automatizaci? Tato akce je nevratná (graf i historie běhů).",
    test: "Test",
    testNothing: "Žádná akce by se nespustila.",
    testWould: (what: string) => `Vystřelilo by: ${what}`,
    edit: "Upravit",
    run: "Spustit teď",
    runOk: "Automatizace spuštěna — notifikace by měla dorazit během chvilky.",
    runNoManualNode: "Přidej do automatizace uzel Tlačítko / test, ať ji jde spustit ručně.",
    // Popisky uzlů, portů a parametrů žijí ve sdíleném `globals/automations/labels.ts`,
    // protože je potřebuje i validace grafu (běží na serveru i v klientovi) — chybová
    // hláška tak umí říct „chybí Veličina" místo id uzlu. Re-export drží konvenci
    // „UI stringy skrz dictionary".
    nodes: NODE_TEXT,
    handles: HANDLE_LABEL,
    fields: PARAM_TEXT,
    dataTypes: DATA_TYPE_LABEL,
    categories: CATEGORY_LABEL,
  },
  computed: {
    title: "Vlastní veličiny",
    subtitle:
      "Počítané hodnoty z jiných veličin (vzorec nebo energie/kWh). Objeví se v grafech i dashboardu jako běžná veličina.",
    addNew: "Nová veličina",
    empty: "Zatím žádné vlastní veličiny.",
    adminOnly: "Vytváření vlastních veličin může jen administrátor.",
    badge: "počítaná",
    name: "Název",
    slug: "Identifikátor (slug)",
    slugHint:
      "Malá písmena, číslice, pomlčka a lomítko (úrovně MQTT). Použije se v topicu zige/calc/<slug>/val — např. pip-main/pv-kwh-d.",
    unit: "Jednotka",
    decimals: "Desetinná místa",
    interval: "Min. interval emitu (s)",
    typeFormula: "Vzorec",
    typeIntegrator: "Energie (kWh)",
    formula: "Vzorec",
    formulaHint: "Použij {topic} pro hodnotu jiné veličiny, např. {zige/pip-1/pv-w/val} + {zige/tracer-1/pv-w/val}.",
    insertTopic: "+ veličina",
    integrandTopic: "Zdroj výkonu (W)",
    resetWindow: "Reset počítadla",
    period: { NONE: "Bez resetu (celkem)", DAILY: "Denní", WEEKLY: "Týdenní", MONTHLY: "Měsíční", YEARLY: "Roční" },
    lastValue: "Aktuální hodnota",
    recompute: "Přepočítat",
    recomputeConfirm: "Přepočítat aktuální okno ze zdrojových dat?",
    reset: "Vynulovat",
    resetConfirm: "Vynulovat aktuální počítadlo na nulu?",
    deleteConfirm: "Smazat veličinu?",
    deleteKeepData: "Ponechat historická data (jen vypnout počítání)",
    saved: "Veličina uložena.",
    created: "Veličina vytvořena.",
    deleted: "Veličina smazána.",
    saveError: "Uložení se nepovedlo.",
  },

  // Systémový monitor (btop-like) — docs/plans/SYSTEM-MONITOR-BTOP.md
  /**
   * Plocha, kterou se nepodařilo zobrazit.
   *
   * ⚠ Tyhle hlášky nahradily TICHÉ dosazení výchozího layoutu z kódu. Uživatel dřív viděl
   * jiný dashboard, než jaký je v databázi, a nic tomu nenasvědčovalo.
   */
  layout: {
    unauthorized: "Pro zobrazení rozložení se přihlas.",
    loadFailed: "Rozložení se nepodařilo načíst.",
    missing: (key: string) => `Plocha „${key}“ v databázi není.`,
    missingHint: "Pravděpodobně neproběhla migrace databáze — spusť „npm run migrate:deploy“.",
    corrupted: "Uložené rozložení je poškozené.",
    corruptedHint: "Obnov ho v Nastavení → Layout tlačítkem „Obnovit výchozí“.",
  },

  system: {
    title: "Systém",
    subtitle: "Stav serveru v reálném čase",
    adminOnly: "Systémový monitor je dostupný jen správcům.",
    noData: "Zatím nedorazila žádná data.",
    errorHosts: "Seznam strojů se nepodařilo načíst.",
    stale: "Zastaralé",
    paused: "Pozastaveno",
    unavailable: "Nedostupné",

    host: {
      label: "Stroj",
      online: "Online",
      offline: "Nedostupný",
      switchTo: (name: string) => `Přepnout na ${name}`,
      offlineNotice: (name: string, age: string) => `${name} neodpovídá — poslední data před ${age}.`,
      neverSeen: (name: string) => `${name} zatím neposlal žádná data.`,
      dataAge: (age: string) => `data stará ${age}`,
      hostname: "Název",
      os: "Systém",
      kernel: "Jádro",
      model: "Model",
      arch: "Architektura",
      nodeVersion: "Node",
    },

    cpu: {
      title: "Procesor",
      total: "Celkem",
      cores: "Jádra",
      core: (i: number) => `Jádro ${i}`,
      frequency: "Frekvence",
      temperature: "Teplota",
      uptime: "Běží",
      load: "Průměrná zátěž (1 / 5 / 15 min)",
      processes: "Procesů",
      running: "Běžících",
      breakdown: "Rozpad vytížení",
      user: "Uživatel",
      nice: "Nice",
      system: "Systém",
      iowait: "Čekání na I/O",
      irq: "Přerušení",
      softirq: "Sw. přerušení",
      steal: "Odebraný čas",
      pressure: "Tlak na CPU",
      warmingUp: "Měřím…",
    },

    mem: {
      title: "Paměť",
      total: "Celkem",
      used: "Použito",
      usedNoCache: "Použito bez cache",
      available: "Dostupné",
      free: "Volné",
      cached: "Vyrovnávací paměť",
      buffers: "Buffery",
      shared: "Sdílené (tmpfs)",
      usedOf: (used: string, total: string) => `${used} z ${total}`,
      swapTitle: "Odkládací prostor",
      swapNone: "Odkládací prostor není nastavený.",
      swapZram: "zram — komprimovaná RAM, ne disk",
      swapKindPartition: "oddíl",
      swapKindFile: "soubor",
      /** Bez tohohle vypadá 22 % swapu jako problém, přestože data zabírají čtvrtinu. */
      swapCompressedTo: (size: string) => `zabírá ${size} paměti`,
    },

    disk: {
      title: "Disky",
      none: "Žádné disky k zobrazení.",
      free: "Volno",
      busy: "Vytížení",
      /** Diskstats jsou per zařízení, ne per oddíl — bez tohohle vypadají shodná čísla jako chyba. */
      ioWholeDevice: (device: string) => `I/O je za celé zařízení ${device}, ne jen za tento oddíl.`,
    },

    net: {
      title: "Síť",
      auto: "Automaticky",
      none: "Žádné aktivní rozhraní.",
      up: "Připojeno",
      down: "Odpojeno",
      totalDown: "Celkem přijato",
      totalUp: "Celkem odesláno",
      errors: "Chyby (↓/↑)",
      dropped: "Zahozeno (↓/↑)",
      scaleMax: (value: string) => `rozsah do ${value}`,
    },

    sensors: {
      title: "Čidla",
      none: "Žádná teplotní čidla nejsou dostupná.",
      critical: "Kritická",
    },

    rpi: {
      title: "Raspberry Pi",
      throttling: "Omezování výkonu",
      ok: "Bez omezení",
      now: "teď",
      ever: "od zapnutí",
      undervoltage: "Podpětí",
      freqCapped: "Snížená frekvence",
      throttled: "Omezený výkon",
      softTempLimit: "Teplotní strop",
      onlyUndervoltage: "Toto jádro nehlásí ostatní důvody omezení, jen podpětí.",
      fan: "Ventilátor",
      fanRpm: "ot/min",
      fanOff: "Stojí",
      tripPoints: "Teplotní meze",
      unavailable: "Tento stroj nehlásí throttling ani ventilátor.",
      power: "Příkon desky",
      input5v: "Vstupní napětí",
      /** Přímé měření zdroje je užitečnější než binární příznak podpětí. */
      input5vHint: "Měřeno na 5V railu PMIC. Pod 4,8 V hrozí podpětí.",
      sourceVcgencmd: "z vcgencmd",
      sourceSysfs: "ze sysfs",
    },

    app: {
      title: "Aplikace Demirel",
      pid: "PID",
      uptime: "Běží",
      rss: "Paměť procesu",
      heap: "Halda",
      cpu: "Vytížení CPU",
      eventLoop: "Zpoždění smyčky",
      eventLoopHint: "Jak dlouho čeká úloha na zpracování. Nad 100 ms se aplikace zadrhává.",
      resources: "Otevřené zdroje",
      pm2Restarts: "Restartů (při startu)",
      dbPool: "Databázový fond",
      dbPoolWaiting: "Čeká",
      dbPoolIdle: "Nečinné",
      pgTx: "Transakcí/s",
      pgCacheHit: "Zásah cache",
      mqttQueue: "Fronta MQTT",
      mqttDropped: "Zahozeno",
      mqttGateDropped: "Zahozeno před frontou",
      /** Vysvětluje, proč se to liší od „Zahozeno" — jinak to vypadá jako totéž číslo dvakrát. */
      mqttGateDroppedHint: "aplikace nedokončila start",
      outbox: "Fronta notifikací",
      wsClients: "Připojených klientů",
      modules: "Moduly",
      moduleRunning: "Běží",
      moduleOffline: "Vypnuto",
      moduleError: "Chyba",
    },

    proc: {
      title: "Procesy",
      search: "Hledat proces, příkaz nebo uživatele…",
      clearFilter: "Zrušit hledání",
      tree: "Strom",
      kernel: "Jádrová vlákna",
      perCore: "% vůči jádru",
      loadMore: "Načíst více",
      shown: (shown: number, total: number) => `${shown} z ${total} procesů`,
      none: "Žádný proces neodpovídá hledání.",
      redacted: "Citlivé části příkazu jsou skryté.",
      ownProcess: "tato aplikace",
      pid: "PID",
      name: "Proces",
      cmd: "Příkaz",
      user: "Uživatel",
      threads: "Vlákna",
      mem: "Paměť",
      cpu: "CPU",
      time: "Čas",
      state: "Stav",
      started: "Spuštěn",
      nice: "Priorita",
      lastCore: "Poslední jádro",
      detailTitle: "Detail procesu",
      copyCmd: "Kopírovat příkaz",
      copied: "Zkopírováno",
      states: {
        R: "Běží",
        S: "Spí",
        D: "Čeká na I/O",
        Z: "Zombie",
        T: "Zastaven",
        t: "Krokován",
        X: "Ukončen",
        I: "Nečinný",
        "?": "Neznámý",
      },
    },

    mqtt: {
      title: "MQTT",
      brokerTitle: "Broker",
      noBroker: "Tento stroj není připojený k brokeru.",
      /** `$SYS` chodí po `sys_interval` (default 10 s), takže krátké stáří je normální. */
      brokerStale: (age: string) => `data z brokeru stará ${age}`,
      version: "Verze",
      uptime: "Běží",
      clients: "Klientů",
      clientsMax: "Maximum",
      subscriptions: "Odběrů",
      retained: "Retained zpráv",
      msgIn: "Zpráv přijato",
      msgOut: "Zpráv odesláno",
      bytesIn: "Přeneseno dolů",
      bytesOut: "Přeneseno nahoru",
      dropped: "Zahozeno",
      heap: "Paměť brokeru",

      connections: "Připojení",
      colClient: "Klient",
      colIp: "IP adresa",
      colProtocol: "Protokol",
      colUser: "Uživatel",
      colKeepalive: "Keepalive",
      colConnected: "Připojen",
      colMac: "MAC",
      colVendor: "Výrobce",
      /** Zařízení je na síti, ale nemá spojení na broker — nejzajímavější stav ze všech. */
      lanTitle: "Na síti bez MQTT",
      lanNone: "Všechna nalezená zařízení mají spojení na broker.",
      lanHint: "Zařízení odpovídá na síti, ale nepřipojilo se k brokeru.",
      /** Host, jehož MAC sedí s kamerou v databázi — kamery s brokerem nemluví. */
      cameraLabel: "kamera",
      sourceArp: "z ARP",
      sourceScan: "ze skenu",
      noClients: "Žádná aktivní spojení.",
      localClient: "tento stroj",
      /** Identita přijde z `$SYS/broker/log/N` až při dalším připojení klienta. */
      unknownClient: "neznámý",
      unknownClientHint: "Client ID se doplní, až se klient znovu připojí.",
      /**
       * Odkud se ví, že za spojením sedí tohle zařízení. Rozlišuje se schválně: `diag` je
       * tvrzení samotného zařízení, client ID je jen odvození z tvaru jména.
       */
      linkVia: (via: "diag-ip" | "client-id") =>
        via === "diag-ip"
          ? "Zařízení tuhle adresu samo hlásí v diagnostice."
          : "Odvozeno z client ID; zařízení svoji adresu nehlásí.",
      colDeviceLink: "Zařízení",
      protocolLabel: (v: number | null) =>
        v === 5 ? "MQTT 5" : v === 4 ? "MQTT 3.1.1" : v === 3 ? "MQTT 3.1" : "neznámý",

      devices: "Zařízení",
      colDevice: "Zařízení",
      colTopics: "Metrik",
      colLastSeen: "Poslední hodnota",
      colState: "Stav",
      colDeviceIp: "IP adresa",
      /** Zařízení publikuje `diag`, ale k brokeru zrovna nemá spojení (LWT ještě nedoběhl). */
      noConnection: "bez spojení",
      noDevices: "Žádná zařízení neposlala konfiguraci.",
      online: "Online",
      offline: "Offline",
      hasConfig: "Konfigurace uložena",
      rssi: "Signál",
      memFree: "Volná paměť",
      deviceUptime: "Uptime",
      resetCause: "Poslední reset",
      bootCount: "Bootů",
      firmware: "Firmware",
      /** Diagnostika DS18B20 — kolik ROM sběrnice reálně vidí. */
      onewire: "1-Wire čidel",
      noDiag: "Zařízení zatím neposílá telemetrii.",
    },

    usb: {
      title: "USB zařízení",
      none: "Na sběrnici není nic k vidění.",
      /** Horní část boxu — jen převodníky, které někdo používá nebo používat má. */
      serialTitle: "Sériové porty",
      serialNone: "Žádný USB-sériový převodník.",
      treeTitle: "Sběrnice",
      colDevice: "Zařízení",
      colDriver: "Ovladač",
      colSpeed: "Rychlost",
      colPower: "Odběr",
      colPort: "Port",
      colClaim: "Používá",
      /**
       * ⚠ Nesmí říkat „nepoužívá se". Nárok se odvozuje z KONFIGURACE zařízení v databázi,
       * ne z toho, kdo má port doopravdy otevřený (`/proc/<pid>/fd` se v tomhle projektu
       * zásadně nečte). Chybějící nárok tedy znamená „žádný driver ho nemá nastavený" —
       * tvrdit „nepoužívá se" by byla lež pokaždé, když port drží něco mimo Demirel.
       */
      unclaimed: "není v konfiguraci",
      unclaimedHint: "Žádný driver nemá tenhle port nastavený jako svoje zařízení.",
      /** Vazba na síťové rozhraní / blokové zařízení za tímhle USB kusem. */
      linkNet: "Rozhraní",
      linkBlock: "Disk",
      noSerial: "bez sériového čísla",
      /** Nejdůležitější hláška celé sekce — viz varování níž. */
      noSerialHint:
        "Zařízení nehlásí sériové číslo, takže ho nejde odlišit od druhého kusu téhož typu ani přes /dev/serial/by-id.",
      missingPort: "port neexistuje",
      missingPortHint: (path: string) => `V konfiguraci je ${path}, ale na sběrnici takový port není.`,
      driverDown: "driver neběží",
      driverDownHint: "Zařízení je připojené, ale služba, která ho má obsluhovat, nejede.",
      byIdTitle: "Stabilní cesta",
      /** Odběr sečtený přes zařízení pod jedním hubem. */
      powerTotal: (ma: string) => `celkem ${ma}`,
      speedFor: (mbps: number) => (mbps >= 1000 ? `${mbps / 1000} Gb/s` : `${mbps} Mb/s`),
      hub: "hub",
    },

    caps: {
      title: "Omezení tohoto stroje",
    },

    /**
     * Jak je zařízení připojené. ⚠ Nikde tu NESMÍ padnout slovo „ethernet".
     *
     * Jistota je jen jedna: když MAC sedí v registrační tabulce AP, je zařízení na naší
     * wifi. Všechno ostatní vidíme až za portem `ether1`, kam ústí jak drát, tak wifi
     * routeru Archer — a ten žádné API nemá, takže tyhle dva případy od sebe rozlišit
     * neumíme. Napsat „ethernet" by tedy byla u poloviny zařízení lež.
     */
    link: {
      wifiAp: "Wi-Fi (AP)",
      uplink: "za uplinkem",
      uplinkHint: "Drát, nebo Wi-Fi routeru — to už AP nepozná, obojí přichází portem ether1.",
      unknown: "neznámé",
      colLink: "Připojení",
      /** Bez AP na síti se o způsobu připojení netvrdí vůbec nic. */
      noData: "AP neodpovídá, způsob připojení se nedá zjistit.",
      signal: "Signál",
      rate: "Rychlost (↓/↑)",
      assocFor: (age: string) => `asociován ${age}`,
    },

    routeros: {
      title: "MikroTik",
      identity: "Identita",
      model: "Model",
      version: "RouterOS",
      firmware: "RouterBOOT",
      /** Nejčastější příčina divného chování AP: RouterBOOT zaostal za RouterOS. */
      firmwareOutdated: (v: string) => `k dispozici ${v}`,
      address: "Adresa",
      /** Podstatné: adresa nikde nestojí zapsaná, zařízení se ohlásilo samo. */
      via: (v: "mndp-identity" | "mndp-mac" | "mndp-single" | "arp-mac") =>
        v === "arp-mac" ? "nalezeno skenem podle MAC" : "ohlásilo se samo (MNDP)",

      radiosTitle: "Rádia",
      readingsTitle: "Napájení a čidla",
      /** Jmenné popisky ze `/system/health` — RouterOS je posílá anglicky. */
      readingLabel: (name: string) =>
        name === "voltage" ? "Vstupní napětí" : name === "current" ? "Proud" : name,
      badBlocks: "Vadné bloky flash",
      writeSectors: "Zapsáno na flash",
      writeSectorsUnit: "sektorů celkem",
      /** ⚠ Rozlišení, které stálo za chybu v prvním návrhu — viz komentář u `hasPeers`. */
      radioOffline: "vypnuto",
      radioNoPeers: "bez klientů",
      radioNoPeersHint:
        "Rádio vysílá, jen na něm zrovna nikdo není. RouterOS to hlásí jako `running=false`, "
        + "což NEZNAMENÁ vypnuté rádio.",
      colState: "Stav",
      colLinkDowns: "Výpadky",
      /** „Flapping" = spojení opakovaně padá a znovu se navazuje. */
      linkDownsHint: (n: number) =>
        `Síť se ${n}× vyprázdnila (poslední klient odešel). Vysoké číslo znamená, že klientům padá spojení.`,
      radioOff: "vypnuto",
      colRadio: "Rádio",
      colSsid: "SSID",
      colBand: "Pásmo",
      colChannel: "Kanál",
      colTxPower: "Výkon",
      colPeers: "Klientů",
      noRadios: "Žádná rádia.",

      wifiTitle: "Klienti Wi-Fi",
      noClients: "Na wifi není připojený nikdo.",
      colMac: "MAC",
      colIp: "IP adresa",
      colSignal: "Signál",
      /** Z pohledu KLIENTA: co mu AP posílá, je jeho download. */
      colTxRate: "↓ ke klientovi",
      colRxRate: "↑ od klienta",
      colUptime: "Připojen",
      cameraLabel: "kamera",
      /** IP se doplňuje z ARP tabulky RPi — AP samo adresy klientů nezná. */
      ipUnknown: "IP se nepodařilo spárovat",

      logTitle: "Log AP",
      noLogs: "Log je prázdný.",
      colTime: "Čas",
      colTopics: "Téma",
      colMessage: "Zpráva",
      logHint: "Posledních 120 řádků kruhového bufferu na AP.",
      /** Bez tohohle by se čas v logu tiše porovnával s časem prohlížeče. */
      logTimezone: (tz: string) => `Časy jsou v zóně AP (${tz}), ne v čase tvého prohlížeče.`,
    },
  },
} as const;
